import { UntypedFormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import dayjs, { Dayjs } from 'dayjs';

/** Marks a field incomplete when nothing was entered or no files were uploaded. */
export const requiredFileList: ValidatorFn = (control: UntypedFormControl): ValidationErrors | null => {
  const value = control.value;
  if (value == null) {
    return { required: true };
  }
  if (typeof value === 'string' && value.trim() === '') {
    return { required: true };
  }
  if (Array.isArray(value) && value.length === 0) {
    return { required: true };
  }
  return null;
};

/** Returns an error if value is LESS than 0 */
export const mustBeSelected: ValidatorFn = (control: UntypedFormControl): ValidationErrors | null => {
  return +control.value < 0 ? { required: true } : null;
};

/** Returns an error if value is EQUAL TO 0 */
export const mustNotBeZero: ValidatorFn = (control: UntypedFormControl): ValidationErrors | null => {
  return +control.value == 0 ? { required: true } : null;
};

/**
 * Returns form error 'complexityRequired' if the new password does not match the complexity level.
 */
export const passwordComplexity: ValidatorFn = (control: UntypedFormControl): ValidationErrors | null => {
  const pw: string = control.value ?? '';
  const errorObj: { [key: string]: boolean } = {};
  if (pw.length < 8) {
    errorObj.requiredLength = true;
  }

  const lowerCaseMatch = pw.match(/[a-z]/);
  const upperCaseMatch = pw.match(/[A-Z]/);
  const numberMatch = pw.match(/[0-9]/);
  const spCharMatch = pw.match(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]|\s/);
  let invalid = 0;
  if (!lowerCaseMatch) {
    invalid++;
  }
  if (!upperCaseMatch) {
    invalid++;
  }
  if (!numberMatch) {
    invalid++;
  }
  if (!spCharMatch) {
    invalid++;
  }

  // Check if this is VALID
  if (invalid > 1) {
    errorObj.complexityRequired = true;
  }

  return Object.keys(errorObj).length > 0 ? errorObj : null;
};

/**
 * Returns a ValidatorFn that can validate a date based on a min or max date.
 * Defaults to a valid cutoff of 100 years in the past and future if no valid args are passed in.
 * @param maxDate
 * @param minDate
 * @returns Validation Errors or null if there are no errors.
 */
export const dateValidator = (maxDate?: Dayjs, minDate?: Dayjs): ValidatorFn => {
  const defaultValidYears = 100;
  if (minDate == undefined || !minDate.isValid) {
    minDate = dayjs().subtract(defaultValidYears, 'year');
  }
  if (maxDate == undefined || !maxDate.isValid) {
    maxDate = dayjs().add(defaultValidYears, 'year');
  }

  return (control: UntypedFormControl): ValidationErrors | null => {
    const date = dayjs(control.value);
    if (!date.isValid()) return null; // allow empty values.
    if (date.isAfter(maxDate) || date.isBefore(minDate)) return { invalidDate: 'Please enter a valid date.' };
    return null;
  };
};

export const validEmailRegex = (emailRequired = true): ValidatorFn => {
  return (control: UntypedFormControl): ValidationErrors | null => {
    const email = control.value;
    if (email == '' || email == undefined || email == null) {
      return emailRequired ? { required: 'Please enter an email' } : null;
    }
    // new RegExp(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/).test(email);
    return /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(email)
      ? null
      : { invalidEmail: 'Please enter a valid email' };
  };
};
